import type { Block } from "../content/types";
import { prayer as morningPrayer } from "./morning-prayer";
import { prayer as prayerBeforeAnExam } from "./prayer-before-an-exam";
import { prayer as prayerBeforeBed } from "./prayer-before-bed";
import { prayer as prayerBeforeWork } from "./prayer-before-work";
import { prayer as prayerBeforeYouOpenYourPhone } from "./prayer-before-you-open-your-phone";
import { prayer as prayerForAFriend } from "./prayer-for-a-friend";
import { prayer as prayerForANewStart } from "./prayer-for-a-new-start";
import { prayer as prayerForAnger } from "./prayer-for-anger";
import { prayer as prayerForAnxiety } from "./prayer-for-anxiety";
import { prayer as prayerForComparison } from "./prayer-for-comparison";
import { prayer as prayerForFocus } from "./prayer-for-focus";
import { prayer as prayerForForgiveness } from "./prayer-for-forgiveness";
import { prayer as prayerForGratitude } from "./prayer-for-gratitude";
import { prayer as prayerForGrief } from "./prayer-for-grief";
import { prayer as prayerForGuidance } from "./prayer-for-guidance";
import { prayer as prayerForHope } from "./prayer-for-hope";
import { prayer as prayerForLoneliness } from "./prayer-for-loneliness";
import { prayer as prayerForMyFamily } from "./prayer-for-my-family";
import { prayer as prayerForPatience } from "./prayer-for-patience";
import { prayer as prayerForSelfControl } from "./prayer-for-self-control";
import { prayer as prayerForStrength } from "./prayer-for-strength";
import { prayer as prayerWhenYouCantPray } from "./prayer-when-you-cant-pray";
import type { Prayer } from "./types";

/**
 * Every prayer, in the order `/prayers` lists them.
 *
 * The order is not alphabetical and not chronological: it is roughly the order
 * somebody arrives at them, starting with the situations people search for at
 * two in the morning. A hub that opens with "a prayer for gratitude" is a hub
 * written for the person who is already fine.
 */
export const PRAYERS: Prayer[] = [
  morningPrayer,
  prayerBeforeAnExam,
  prayerBeforeBed,
  prayerBeforeWork,
  prayerBeforeYouOpenYourPhone,
  prayerForAFriend,
  prayerForANewStart,
  prayerForAnger,
  prayerForAnxiety,
  prayerForComparison,
  prayerForFocus,
  prayerForForgiveness,
  prayerForGratitude,
  prayerForGrief,
  prayerForGuidance,
  prayerForHope,
  prayerForLoneliness,
  prayerForMyFamily,
  prayerForPatience,
  prayerForSelfControl,
  prayerForStrength,
  prayerWhenYouCantPray,
];

export const PRAYERS_BY_SLUG: Record<string, Prayer> = Object.fromEntries(
  PRAYERS.map((p) => [p.slug, p]),
);

export function prayerBySlug(slug: string): Prayer | undefined {
  return PRAYERS_BY_SLUG[slug];
}

/** The path a prayer lives at. Nothing hand-writes one. */
export function prayerPath(slug: string): string {
  return `/prayers/${slug}`;
}

/**
 * Roughly how long the page takes to read.
 *
 * Counts the prayer itself, which is most of the page and is the part people
 * actually read aloud, plus the notes, the honesty section and the FAQ. Same
 * decision as `passageMinutes`: a reading time that ignores half the words is
 * not an estimate.
 */
export function prayerMinutes(item: Prayer): number {
  const prose = item.body.reduce((n, block) => n + blockWords(block), 0);
  const spoken =
    item.prayer.reduce((n, line) => n + count(line), 0) + count(item.short);
  const around =
    item.when.reduce((n, line) => n + count(line), 0) +
    item.honest.reduce((n, line) => n + count(line), 0) +
    item.takeaways.reduce((n, line) => n + count(line), 0) +
    item.verses.reduce(
      (n, pick) => n + count(pick.verse.text) + count(pick.note),
      0,
    ) +
    (item.faq ?? []).reduce((n, e) => n + count(e.q) + count(e.a), 0);
  return Math.max(1, Math.round((prose + spoken + around) / 220));
}

function count(text: string): number {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

function blockWords(block: Block): number {
  switch (block.type) {
    case "ul":
    case "ol":
      return count(block.items.join(" "));
    case "verse":
      return count(block.verse.text);
    default:
      return count(block.text);
  }
}

/** The `<h2>`s of a prayer's prose, for its table of contents. */
export function prayerOutline(item: Prayer): { id: string; text: string }[] {
  return item.body.flatMap((block) =>
    block.type === "h2" ? [{ id: block.id, text: block.text }] : [],
  );
}

export type { Prayer, PrayerVerse } from "./types";
